/**
 * Left-to-right collapsible EBR walk (console CollapsibleTreeDiagram),
 * restyled for C3. Layer fills follow Investigation Workspace LayerShapeLegend
 * (infra blue, business green, threat red) — not console's green-infra tree.
 *
 * Legend decisions pending:
 * - Crown path uses the --high token (console used pink).
 * - DomainLegend is omitted here; this tree is layer-colored, not domain-colored.
 */

import * as d3 from 'd3';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { prefersReducedMotion } from '../../lib/motion';
import type { CrownPathSegment, EbrWalkHierarchyNode, EbrWalkLayer } from './ebrTreeGraph';

export const EBR_LAYER_COLORS = {
  threat: 'var(--layer-threat)',
  infrastructure: 'var(--layer-infra)',
  business: 'var(--layer-business)',
  crown: 'var(--high)',
  unconfirmed: 'var(--t3)',
};

const LAYER_COLORS: Record<string, string> = EBR_LAYER_COLORS;

const ROOT_VERTICAL_OFFSET = -24;
const CANVAS_BG = 'var(--s1)';
const SELECTED_STROKE = 'var(--t1)';

/** Intro phase 1: reveal one tree depth (L→R). */
const INTRO_DEPTH_MS = 560;
/** Intro phase 2: draw one crown-path hop. */
const INTRO_CROWN_HOP_MS = 840;
const INTRO_PHASE_GAP_MS = 240;

function svgPathLength(el: SVGPathElement | null): number {
  if (!el) return 0;
  try {
    return el.getTotalLength() || 0;
  } catch {
    return 0;
  }
}

type CollapseNode = d3.HierarchyPointNode<EbrWalkHierarchyNode> & {
  _children?: CollapseNode[] | null;
  x0?: number;
  y0?: number;
};

type NodeShape = 'diamond' | 'square' | 'circle';

interface EbrWalkTreeProps {
  data: EbrWalkHierarchyNode | null;
  selectedNodeId?: string | null;
  onSelectNode?: (id: string, name: string, layer: EbrWalkLayer) => void;
  className?: string;
  crownPathSegments?: readonly CrownPathSegment[];
  crownJewelId?: string | null;
  /** Bump to replay the intro (e.g. after Recalculate EBR). */
  introKey?: string | number;
}

function layerColor(layer: string | undefined): string {
  if (layer && LAYER_COLORS[layer]) return LAYER_COLORS[layer];
  return 'var(--t3)';
}

function shortLabel(name: string, maxLen = 48): string {
  const text = String(name || '').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, Math.max(1, maxLen - 1))}…`;
}

function nodeLayer(d: Pick<CollapseNode, 'data'>): string | undefined {
  return d.data.layer;
}

function isThreatRoot(d: Pick<CollapseNode, 'depth' | 'data'>): boolean {
  return d.depth === 0 && nodeLayer(d) === 'threat';
}

function linkDashPattern(d: d3.HierarchyPointLink<EbrWalkHierarchyNode>): string | null {
  if (d.target.data.unconfirmed) return '4 4';
  if (isThreatRoot(d.source as CollapseNode)) return '6 4';
  return null;
}

function layoutY(d: Pick<CollapseNode, 'depth' | 'x'> & { x0?: number }): number {
  const x = typeof d.x0 === 'number' ? d.x0 : d.x;
  return x + (d.depth === 0 ? ROOT_VERTICAL_OFFSET : 0);
}

function nodeShape(d: CollapseNode): NodeShape {
  const layer = nodeLayer(d);
  if (layer === 'threat') return 'diamond';
  if (layer === 'business') return 'square';
  return 'circle';
}

function markerSize(d: CollapseNode): number {
  if (d.depth === 0) return 9;
  return 6.5;
}

function markerPath(shape: NodeShape, size: number): string {
  if (size <= 0) return 'M 0 0';
  if (shape === 'diamond') {
    const r = size * 1.25;
    return `M 0 ${-r} L ${r} 0 L 0 ${r} L ${-r} 0 Z`;
  }
  if (shape === 'square') {
    const s = size * 0.9;
    return `M ${-s} ${-s} L ${s} ${-s} L ${s} ${s} L ${-s} ${s} Z`;
  }
  return `M 0,0 m -${size},0 a ${size},${size} 0 1,0 ${size * 2},0 a ${size},${size} 0 1,0 -${size * 2},0`;
}

function applyMarkerAttrs(
  selection: d3.Selection<SVGPathElement, CollapseNode, any, any>,
  opts?: {
    selectedId?: string | null;
    onPath?: (id: string) => boolean;
    isJewel?: (id: string) => boolean;
    crownColor?: string;
  }
) {
  const selectedId = opts?.selectedId ?? null;
  const onPath = opts?.onPath ?? (() => false);
  const isJewel = opts?.isJewel ?? (() => false);
  const crownColor = opts?.crownColor ?? LAYER_COLORS.crown;

  selection
    .attr('d', (d) => markerPath(nodeShape(d), markerSize(d)))
    .attr('fill', (d) => layerColor(d.data.layer))
    .attr('stroke', (d) => {
      if (selectedId && d.data.id === selectedId) return SELECTED_STROKE;
      if (isJewel(d.data.id) || onPath(d.data.id)) return crownColor;
      return CANVAS_BG;
    })
    .attr('stroke-width', (d) => {
      if (selectedId && d.data.id === selectedId) return 2.5;
      if (isJewel(d.data.id)) return 2.5;
      if (onPath(d.data.id)) return 2;
      return 1.2;
    });
}

function expandAll(node: CollapseNode) {
  if (node._children) {
    node.children = node._children;
    node._children = null;
  }
  node.children?.forEach((child) => expandAll(child as CollapseNode));
}

function computeViewBox(nodes: CollapseNode[], labelPadLeft = 260, labelPadRight = 48) {
  if (!nodes.length) return { x: 0, y: 0, width: 640, height: 320 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const d of nodes) {
    const y = layoutY(d);
    minX = Math.min(minX, y);
    maxX = Math.max(maxX, y);
    minY = Math.min(minY, d.y);
    maxY = Math.max(maxY, d.y);
  }
  const padY = 36;
  return {
    x: minY - labelPadLeft,
    y: minX - padY,
    width: Math.max(320, maxY - minY + labelPadLeft + labelPadRight),
    height: Math.max(160, maxX - minX + padY * 2),
  };
}

function applyViewBox(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  box: { x: number; y: number; width: number; height: number }
) {
  svg.attr('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
  svg
    .select('rect.zoom-surface')
    .attr('x', box.x)
    .attr('y', box.y)
    .attr('width', box.width)
    .attr('height', box.height);
}

export function EbrWalkLegend({ showUnconfirmed = false }: { showUnconfirmed?: boolean }) {
  return (
    <div className="flex flex-wrap gap-3 text-[12px] text-c3-mute">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rotate-45 border border-c3-mute/50"
          style={{ backgroundColor: LAYER_COLORS.threat }}
        />
        Threat
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full border border-c3-mute/50"
          style={{ backgroundColor: LAYER_COLORS.infrastructure }}
        />
        Infrastructure
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded border border-c3-mute/50"
          style={{ backgroundColor: LAYER_COLORS.business }}
        />
        Business
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-0.5 w-5" style={{ backgroundColor: LAYER_COLORS.crown }} />
        Path to crown jewel
      </span>
      {showUnconfirmed ? (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-5 border-t border-dashed"
            style={{ borderColor: LAYER_COLORS.unconfirmed, background: 'transparent' }}
          />
          Unconfirmed match
        </span>
      ) : null}
    </div>
  );
}

const EbrWalkTree: React.FC<EbrWalkTreeProps> = ({
  data,
  selectedNodeId = null,
  onSelectNode,
  className = '',
  crownPathSegments = [],
  crownJewelId = null,
  introKey = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const rootRef = useRef<CollapseNode | null>(null);
  const updateRef = useRef<(source: CollapseNode) => void>(() => undefined);
  const fitToViewRef = useRef<(() => void) | null>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const zoomTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const selectedIdRef = useRef(selectedNodeId);
  const onSelectRef = useRef(onSelectNode);
  const crownPathSegmentsRef = useRef(crownPathSegments);
  const crownJewelRef = useRef(crownJewelId);
  const dataRef = useRef(data);
  /** After intro phase 2 (or reduced-motion skip), crown path styling is always on. */
  const crownRevealDoneRef = useRef(false);
  /** Nodes reached so far during phase-2 crown draw. */
  const revealedCrownNodesRef = useRef<Set<string>>(new Set());
  const paintNodeStylesRef = useRef<() => void>(() => undefined);

  selectedIdRef.current = selectedNodeId;
  onSelectRef.current = onSelectNode;
  crownPathSegmentsRef.current = crownPathSegments;
  crownJewelRef.current = crownJewelId;
  dataRef.current = data;

  const crownPathKey = useMemo(
    () =>
      `${crownJewelId || ''}:${(crownPathSegments || [])
        .map((seg) => `${seg.from}>${seg.to}`)
        .join('|')}`,
    [crownJewelId, crownPathSegments]
  );

  const crownPathNodeIds = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const seg of crownPathSegments || []) {
      for (const id of [seg.from, seg.to]) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
      }
    }
    if (crownJewelId && !seen.has(crownJewelId)) ids.push(crownJewelId);
    return ids;
  }, [crownPathSegments, crownJewelId]);

  const hierarchyKey = useMemo(() => {
    if (!data) return '';
    const childIds = (data.children || []).map((c) => c.id).join(',');
    return `${data.id}:${data.name}:${childIds}`;
  }, [data]);

  useEffect(() => {
    const host = containerRef.current;
    const treeData = dataRef.current;
    if (!host || !treeData) return;

    let cancelled = false;
    zoomTransformRef.current = d3.zoomIdentity;
    crownRevealDoneRef.current = false;
    revealedCrownNodesRef.current = new Set();
    host.replaceChildren();

    const leafCount = Math.max(1, treeData.children?.length ?? 1);
    const hostHeight = host.clientHeight || 320;
    const siblingGap = Math.max(28, Math.min(40, (hostHeight - 40) / leafCount));
    const depthGap = 160;
    const skipMotion = prefersReducedMotion();

    const root = d3.hierarchy(treeData) as CollapseNode;
    root.x0 = 0;
    root.y0 = 0;
    rootRef.current = root;
    expandAll(root);

    const treeLayout = d3.tree<EbrWalkHierarchyNode>().nodeSize([siblingGap, depthGap]);

    const svg = d3
      .create('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr(
        'style',
        'display: block; width: 100%; height: 100%; font: 12px var(--font-sans); touch-action: none; color: var(--t1);'
      )
      .attr('role', 'img')
      .attr('aria-label', 'Effective Blast Radius walk');

    svg
      .append('rect')
      .attr('class', 'zoom-surface')
      .attr('fill', 'transparent')
      .style('cursor', 'grab')
      .style('pointer-events', 'all');

    const viewport = svg.append('g').attr('class', 'collapsible-viewport');
    const linkLayer = viewport.append('g').attr('class', 'collapsible-links');
    const crownPathLayer = viewport.append('g').attr('class', 'collapsible-crown-path');
    const nodeLayer = viewport.append('g').attr('class', 'collapsible-nodes');

    const crownColor = LAYER_COLORS.crown;
    const crownPathNodeSet = () => {
      const set = new Set<string>();
      for (const seg of crownPathSegmentsRef.current || []) {
        set.add(seg.from);
        set.add(seg.to);
      }
      const jewel = crownJewelRef.current;
      if (jewel) set.add(jewel);
      return set;
    };
    const isOnCrownPath = (id: string) => {
      if (crownRevealDoneRef.current) return crownPathNodeSet().has(id);
      return revealedCrownNodesRef.current.has(id);
    };
    const isCrownJewel = (id: string) => {
      if (!isOnCrownPath(id)) return false;
      const jewel = crownJewelRef.current;
      return Boolean(jewel && id === jewel);
    };

    const linkPath = d3
      .linkHorizontal<d3.HierarchyPointLink<EbrWalkHierarchyNode>, CollapseNode>()
      .x((d) => d.y)
      .y((d) => layoutY(d));

    const paintCrownPathOverlay = (nodes: CollapseNode[]) => {
      crownPathLayer.selectAll('*').remove();
      const segments = crownPathSegmentsRef.current || [];
      if (!segments.length) return;
      const byId = new Map(nodes.map((n) => [n.data.id, n]));
      for (const seg of segments) {
        const source = byId.get(seg.from);
        const target = byId.get(seg.to);
        if (!source || !target) continue;
        crownPathLayer
          .append('path')
          .attr('class', 'crown-segment')
          .attr('fill', 'none')
          .attr('stroke', crownColor)
          .attr('stroke-width', 2.75)
          .attr('stroke-opacity', 0.85)
          .attr('stroke-linecap', 'round')
          .attr(
            'd',
            linkPath({
              source,
              target,
            } as d3.HierarchyPointLink<EbrWalkHierarchyNode>)
          );
      }
    };

    const paintNodeStyles = () => {
      const selectedId = selectedIdRef.current;
      nodeLayer.selectAll<SVGGElement, CollapseNode>('g.node').each(function (d) {
        const group = d3.select<SVGGElement, CollapseNode>(this);
        const onPath = isOnCrownPath(d.data.id);
        const isJewel = isCrownJewel(d.data.id);
        applyMarkerAttrs(group.select<SVGPathElement>('path.hit'), {
          selectedId,
          onPath: isOnCrownPath,
          isJewel: isCrownJewel,
          crownColor,
        });
        group.select('circle.crown-ring').remove();
        if (onPath) {
          group
            .insert('circle', 'path.hit')
            .attr('class', 'crown-ring')
            .attr('r', isJewel ? 14 : 11)
            .attr('fill', 'none')
            .attr('stroke', crownColor)
            .attr('stroke-width', isJewel ? 2.5 : 1.75)
            .attr('stroke-opacity', isJewel ? 1 : 0.65)
            .style('pointer-events', 'none');
        }
      });
    };
    paintNodeStylesRef.current = paintNodeStyles;

    const finishCrownReveal = () => {
      if (cancelled) return;
      crownRevealDoneRef.current = true;
      Array.from(crownPathNodeSet()).forEach((id) => revealedCrownNodesRef.current.add(id));
      paintNodeStyles();
    };

    const runCrownPathIntro = (nodes: CollapseNode[], phase2StartMs: number) => {
      crownPathLayer.selectAll('*').remove();
      const segments = crownPathSegmentsRef.current || [];
      if (!segments.length) {
        finishCrownReveal();
        return;
      }
      const byId = new Map(nodes.map((n) => [n.data.id, n]));
      let lastHopIndex = -1;

      segments.forEach((seg, hopIndex) => {
        const source = byId.get(seg.from);
        const target = byId.get(seg.to);
        if (!source || !target) return;
        lastHopIndex = hopIndex;
        const pathSel = crownPathLayer
          .append('path')
          .attr('class', 'crown-segment')
          .attr('fill', 'none')
          .attr('stroke', crownColor)
          .attr('stroke-width', 2.75)
          .attr('stroke-opacity', 0.85)
          .attr('stroke-linecap', 'round')
          .attr(
            'd',
            linkPath({
              source,
              target,
            } as d3.HierarchyPointLink<EbrWalkHierarchyNode>)
          );

        const el = pathSel.node();
        const len = svgPathLength(el);
        pathSel
          .attr('stroke-dasharray', `${len} ${len}`)
          .attr('stroke-dashoffset', len)
          .transition()
          .delay(phase2StartMs + hopIndex * INTRO_CROWN_HOP_MS)
          .duration(Math.max(160, INTRO_CROWN_HOP_MS * 0.9))
          .ease(d3.easeCubicOut)
          .attr('stroke-dashoffset', 0)
          .on('start', () => {
            if (cancelled) return;
            revealedCrownNodesRef.current.add(seg.from);
            revealedCrownNodesRef.current.add(seg.to);
            paintNodeStyles();
          });
      });

      if (lastHopIndex < 0) {
        finishCrownReveal();
        return;
      }
      const doneAt = phase2StartMs + (lastHopIndex + 1) * INTRO_CROWN_HOP_MS + 40;
      d3.timeout(() => finishCrownReveal(), doneAt);
    };

    const runWalkIntro = (nodes: CollapseNode[]) => {
      const maxDepth = d3.max(nodes, (d) => d.depth) ?? 0;

      if (skipMotion) {
        linkLayer
          .selectAll<SVGPathElement, d3.HierarchyPointLink<EbrWalkHierarchyNode>>('path.link')
          .attr('d', linkPath)
          .attr('stroke-dasharray', (d) => linkDashPattern(d));
        nodeLayer.selectAll<SVGGElement, CollapseNode>('g.node').attr('opacity', 1);
        nodeLayer.selectAll('text.label').attr('opacity', 1);
        paintCrownPathOverlay(nodes);
        finishCrownReveal();
        return;
      }

      // Phase 1 — walk only (no amber path yet).
      crownPathLayer.selectAll('*').remove();
      revealedCrownNodesRef.current = new Set();
      crownRevealDoneRef.current = false;

      nodeLayer
        .selectAll<SVGGElement, CollapseNode>('g.node')
        .attr('transform', (d) => `translate(${d.y},${layoutY(d)})`)
        .attr('opacity', 0)
        .select<SVGPathElement>('path.hit')
        .attr('d', (d) => markerPath(nodeShape(d), markerSize(d)));

      nodeLayer.selectAll('text.label').attr('opacity', 1);

      linkLayer
        .selectAll<SVGPathElement, d3.HierarchyPointLink<EbrWalkHierarchyNode>>('path.link')
        .attr('d', linkPath)
        .attr('stroke', (d) => (d.target.data.unconfirmed ? LAYER_COLORS.unconfirmed : 'var(--line-2)'))
        .attr('stroke-opacity', (d) => (d.target.data.unconfirmed ? 0.9 : 0.55))
        .each(function (d) {
          const el = this as SVGPathElement;
          const len = svgPathLength(el);
          const depth = d.target.depth;
          const decorative = linkDashPattern(d);
          d3.select(el)
            .attr('stroke-dasharray', `${len} ${len}`)
            .attr('stroke-dashoffset', len)
            .transition()
            .delay(depth * INTRO_DEPTH_MS)
            .duration(Math.max(140, INTRO_DEPTH_MS * 0.85))
            .ease(d3.easeCubicOut)
            .attr('stroke-dashoffset', 0)
            .on('end', function () {
              if (cancelled) return;
              const sel = d3.select(this);
              if (decorative) sel.attr('stroke-dasharray', decorative);
              else sel.attr('stroke-dasharray', null);
            });
        });

      nodeLayer
        .selectAll<SVGGElement, CollapseNode>('g.node')
        .transition()
        .delay((d) => d.depth * INTRO_DEPTH_MS)
        .duration(Math.max(120, INTRO_DEPTH_MS * 0.55))
        .ease(d3.easeCubicOut)
        .attr('opacity', 1);

      paintNodeStyles();

      // Phase 2 — amber crown path(s), after the walk finishes.
      const phase2Start = (maxDepth + 1) * INTRO_DEPTH_MS + INTRO_PHASE_GAP_MS;
      d3.timeout(() => {
        if (cancelled) return;
        runCrownPathIntro(nodes, 0);
      }, phase2Start);
    };

    let introPlayed = false;

    const update = (source: CollapseNode) => {
      const treeRoot = treeLayout(root);
      const nodes = treeRoot.descendants() as CollapseNode[];
      const links = treeRoot.links();
      const viewBox = computeViewBox(nodes);
      applyViewBox(svg, viewBox);

      const node = nodeLayer
        .selectAll<SVGGElement, CollapseNode>('g.node')
        .data(nodes, (d) => d.data.id);

      const nodeEnter = node
        .enter()
        .append('g')
        .attr('class', 'node')
        .attr('transform', () => `translate(${source.y0 ?? source.y},${layoutY(source)})`)
        .attr('opacity', introPlayed ? 1 : 0)
        .style('cursor', 'pointer');

      nodeEnter
        .append('path')
        .attr('class', 'hit')
        .attr('d', (d) => markerPath(nodeShape(d), introPlayed ? markerSize(d) : 1e-6))
        .attr('fill', (d) => layerColor(d.data.layer))
        .attr('stroke', CANVAS_BG)
        .attr('stroke-width', 1.2);

      nodeEnter
        .append('text')
        .attr('class', 'label')
        .attr('dy', '0.31em')
        .attr('x', (d) => ((d as CollapseNode).children || (d as CollapseNode)._children ? -14 : 14))
        .attr('text-anchor', (d) =>
          (d as CollapseNode).children || (d as CollapseNode)._children ? 'end' : 'start'
        )
        .attr('fill', 'var(--t1)')
        .attr('paint-order', 'stroke')
        .attr('stroke', CANVAS_BG)
        .attr('stroke-width', 4)
        .text((d) => shortLabel(d.data.name))
        .attr('opacity', introPlayed ? 1 : 1e-6);

      nodeEnter.append('title').text((d) => d.data.name);

      const nodeMerge = nodeEnter.merge(node);

      nodeMerge.on('click', (event, d) => {
        event.stopPropagation();
        onSelectRef.current?.(d.data.id, d.data.name, d.data.layer);
        paintNodeStyles();
      });

      if (introPlayed) {
        nodeMerge.attr('transform', (d) => `translate(${d.y},${layoutY(d)})`).attr('opacity', 1);
        nodeMerge.select<SVGPathElement>('path.hit').attr('d', (d) => markerPath(nodeShape(d), markerSize(d)));
        nodeMerge.select('text.label').attr('opacity', 1);
      }

      node.exit().remove();

      const link = linkLayer
        .selectAll<SVGPathElement, d3.HierarchyPointLink<EbrWalkHierarchyNode>>('path')
        .data(links, (d) => d.target.data.id);

      const linkEnter = link
        .enter()
        .append('path')
        .attr('class', 'link')
        .attr('fill', 'none')
        .attr('stroke', 'var(--line-2)')
        .attr('stroke-opacity', 0.55)
        .attr('stroke-width', 1.5)
        .attr('d', () => {
          const o = { ...source, x: source.x0 ?? source.x, y: source.y0 ?? source.y };
          return linkPath({ source: o, target: o } as d3.HierarchyPointLink<EbrWalkHierarchyNode>);
        });

      const linkMerge = linkEnter.merge(link);
      if (introPlayed) {
        linkMerge
          .attr('d', linkPath)
          .attr('stroke', (d) => (d.target.data.unconfirmed ? LAYER_COLORS.unconfirmed : 'var(--line-2)'))
          .attr('stroke-opacity', (d) => (d.target.data.unconfirmed ? 0.9 : 0.55))
          .attr('stroke-dasharray', (d) => linkDashPattern(d));
      }

      link.exit().remove();

      nodes.forEach((d) => {
        const n = d as CollapseNode;
        n.x0 = n.x;
        n.y0 = n.y;
      });

      if (!introPlayed) {
        introPlayed = true;
        runWalkIntro(nodes);
      } else {
        if (crownRevealDoneRef.current) paintCrownPathOverlay(nodes);
        paintNodeStyles();
      }
    };

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.35, 4])
      .filter((event) => {
        if (event.type === 'wheel') return true;
        const target = event.target as Element | null;
        if (target?.closest?.('g.node')) return false;
        return !event.ctrlKey && !event.button;
      })
      .on('start', () => {
        svg.select('rect.zoom-surface').style('cursor', 'grabbing');
      })
      .on('end', () => {
        svg.select('rect.zoom-surface').style('cursor', 'grab');
      })
      .on('zoom', (event) => {
        zoomTransformRef.current = event.transform;
        viewport.attr('transform', event.transform.toString());
      });

    const svgNode = svg.node()!;
    svgRef.current = svgNode;
    zoomBehaviorRef.current = zoom;

    const selection = d3.select(svgNode);
    selection.call(zoom).on('wheel.zoom', (event: WheelEvent) => {
      event.preventDefault();
      zoom.scaleBy(
        selection as unknown as d3.Selection<SVGSVGElement, unknown, null, undefined>,
        event.deltaY < 0 ? 1.12 : 1 / 1.12,
        d3.pointer(event, svgNode)
      );
    });

    const fitToView = () => {
      if (!rootRef.current) return;
      const laidOut = treeLayout(rootRef.current);
      const box = computeViewBox(laidOut.descendants() as CollapseNode[]);
      applyViewBox(svg, box);
      zoomTransformRef.current = d3.zoomIdentity;
      selection.call(zoom.transform, d3.zoomIdentity);
    };

    updateRef.current = update;
    update(root);
    fitToViewRef.current = fitToView;
    fitToView();
    requestAnimationFrame(() => fitToView());

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            fitToView();
          })
        : null;
    resizeObserver?.observe(host);

    host.appendChild(svgNode);

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      viewport.selectAll('*').interrupt();
      selection.on('.zoom', null);
      zoomBehaviorRef.current = null;
      svgRef.current = null;
      rootRef.current = null;
      updateRef.current = () => undefined;
      fitToViewRef.current = null;
      paintNodeStylesRef.current = () => undefined;
      host.replaceChildren();
    };
  }, [crownPathKey, hierarchyKey, introKey]);

  useEffect(() => {
    paintNodeStylesRef.current();
  }, [selectedNodeId, crownPathKey, crownJewelId, crownPathNodeIds]);

  const zoomBy = (factor: number) => {
    const svgNode = svgRef.current;
    const zoom = zoomBehaviorRef.current;
    if (!svgNode || !zoom) return;
    d3.select(svgNode).transition().duration(160).call(zoom.scaleBy, factor);
  };

  const resetZoom = () => {
    if (fitToViewRef.current) {
      fitToViewRef.current();
    }
  };

  if (!data) {
    return (
      <div className={`flex h-full min-h-[280px] items-center justify-center text-sm text-c3-mute ${className}`}>
        No tree to render.
      </div>
    );
  }

  return (
    <div className={`relative h-full min-h-[280px] w-full overflow-hidden ${className}`}>
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-md bg-s1/90 p-1">
        <button type="button" className="c3-pill !px-2 !py-1" onClick={() => zoomBy(1.25)} title="Zoom in">
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="c3-pill !px-2 !py-1" onClick={() => zoomBy(1 / 1.25)} title="Zoom out">
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="c3-pill !px-2 !py-1" onClick={resetZoom} title="Fit entire tree">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={containerRef}
        className="h-full w-full"
        title="Click a node to inspect · scroll to zoom · drag to pan"
      />
    </div>
  );
};

export default EbrWalkTree;
